import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { ConfigurationTestResultId } from "@repo/app-types/entities/configurationTestResult";
import type {
  CopilotProposalId,
  CopilotProposalResourceIdByType,
} from "@repo/app-types/entities/copilotProposal";
import type { PlaybookId } from "@repo/app-types/entities/playbook";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import type { StaticAgentInstructionTileKey } from "@repo/app-types/entities/teammateAgent";
import { useEffect } from "react";
import type { RootState } from "../../app/store.js";
import { useAppDispatch } from "../../hooks/hook.js";
import { ConfigurationProposalStatus } from "./configurationProposalCard.js";

/** A row the open editor can show: a copilot proposal, or a saved test result on a playbook node. */
export type EditorViewedId = CopilotProposalId | ConfigurationTestResultId;

/** A resource whose editor puts a proposal into its draft, where Save applies it. */
type DraftEditorResourceType = "signal" | "automation" | "training";

/**
 * What the open editor is on: a playbook's section or criterion, a voice
 * agent's instruction tile, or a signal, automation or training. The chat
 * opens a finished turn's proposal or test result for it without a click.
 */
export type EditorFocus = { hasUnsavedChanges: boolean } & (
  | {
      resource_type: "playbook";
      playbookId: PlaybookId;
      playbookNodeId: string;
    }
  | {
      resource_type: "teammateAgent";
      teammateAgentId: TeammateId;
      tile: StaticAgentInstructionTileKey;
    }
  | {
      [T in DraftEditorResourceType]: {
        resource_type: T;
        resourceId: CopilotProposalResourceIdByType[T];
        /** Whether the draft holds a proposal, which alone makes it unsaved. */
        hasLinkedProposal: boolean;
      };
    }[DraftEditorResourceType]
);

/**
 * The rows the open editor is showing — the proposals in its unsaved draft,
 * or the test result on its selected playbook node — and whether it is
 * saving them. The chat's proposal cards and deep-link lists read it.
 */
export interface EditorProposalsState {
  viewedIds: readonly EditorViewedId[];
  isSaving: boolean;
  focus?: EditorFocus;
}

const initialState: EditorProposalsState = {
  viewedIds: [],
  isSaving: false,
};

const editorProposalsSlice = createSlice({
  name: "editorProposals",
  initialState,
  reducers: {
    reportEditorProposals: (
      _state,
      action: PayloadAction<EditorProposalsState>,
    ) => action.payload,
    clearEditorProposals: () => initialState,
  },
});

export const { reportEditorProposals, clearEditorProposals } =
  editorProposalsSlice.actions;
export const editorProposalsReducer = editorProposalsSlice.reducer;

/**
 * Reports the row the editor is showing — the proposal in its draft, or the
 * test result in its results panel — if any, and whether it is saving it,
 * for as long as the editor is mounted.
 */
export function useReportEditorProposal(
  viewedId: EditorViewedId | undefined,
  isSaving: boolean,
  focus?: EditorFocus,
): void {
  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(
      reportEditorProposals({
        viewedIds: viewedId ? [viewedId] : [],
        isSaving,
        ...(focus ? { focus } : {}),
      }),
    );
  }, [dispatch, viewedId, isSaving, focus]);
  useEffect(
    () => () => {
      dispatch(clearEditorProposals());
    },
    [dispatch],
  );
}

/** Where a row stands in the open editor, if the editor holds it. */
export function selectEditorProposalStatus(
  state: RootState,
  viewedId: EditorViewedId,
):
  | typeof ConfigurationProposalStatus.Viewing
  | typeof ConfigurationProposalStatus.Saving
  | undefined {
  const { viewedIds, isSaving } = state.editorProposals;
  if (!viewedIds.includes(viewedId)) {
    return undefined;
  }
  return isSaving
    ? ConfigurationProposalStatus.Saving
    : ConfigurationProposalStatus.Viewing;
}
